import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const MinuteManagement = ({ client, onTransaction }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [description, setDescription] = useState('');
  const [activeTab, setActiveTab] = useState('add');
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const quickAmounts = {
    add: [15, 30, 60, 120],
    subtract: [15, 30, 60]
  };

  const handleQuickAction = async (type, amount) => {
    await handleTransaction(type, amount, `Quick ${type === 'add' ? 'add' : 'subtract'} - ${amount} minutes`);
  };

  const handleCustomTransaction = async () => {
    const amount = parseInt(customAmount);
    if (!amount || amount <= 0) {
      setFeedback({ type: 'error', message: 'Please enter a valid amount' });
      return;
    }

    if (activeTab === 'subtract' && amount > client?.balance) {
      setFeedback({ type: 'error', message: 'Insufficient balance for this transaction' });
      return;
    }

    await handleTransaction(activeTab, amount, description || `Custom ${activeTab === 'add' ? 'add' : 'subtract'} - ${amount} minutes`);
    setCustomAmount('');
    setDescription('');
  };

  const handleTransaction = async (type, amount, desc) => {
    setIsProcessing(true);
    setFeedback({ type: '', message: '' });

    try {
      const result = await onTransaction(type, amount, desc);
      
      if (result?.success) {
        setFeedback({ 
          type: 'success', 
          message: `Successfully ${type === 'add' ? 'added' : 'subtracted'} ${amount} minutes` 
        });
      } else {
        setFeedback({ 
          type: 'error', 
          message: result?.error || 'Transaction failed' 
        });
      }
    } catch (error) {
      setFeedback({ 
        type: 'error', 
        message: error.message || 'An error occurred during the transaction' 
      });
    } finally {
      setIsProcessing(false);
      
      // Clear feedback after 3 seconds
      setTimeout(() => {
        setFeedback({ type: '', message: '' });
      }, 3000);
    }
  };

  const getBalanceStatus = (balance) => {
    if (balance === 0) return { color: 'text-muted-foreground', bgColor: 'bg-muted-foreground/20' };
    if (balance < 30) return { color: 'text-warning', bgColor: 'bg-warning/20' };
    if (balance < 60) return { color: 'text-accent', bgColor: 'bg-accent/20' };
    return { color: 'text-success', bgColor: 'bg-success/20' };
  };

  const balanceInfo = getBalanceStatus(client?.balance || 0);

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-muted/20 border-b border-border">
        <h2 className="text-xl font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Clock" size={20} />
          <span>Minute Management</span>
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Add or subtract solarium minutes from the client's account
        </p>
      </div>

      <div className="p-6">
        {/* Total Minutes Display */}
        <div className={`${balanceInfo.bgColor} rounded-lg p-4 mb-6 border border-border/50`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Minutes</h3>
              <div className={`text-2xl font-bold font-mono ${balanceInfo.color}`}>
                {client?.balance || 0} minutes
              </div>
            </div>
            <Icon name="Wallet" size={32} className={balanceInfo.color} />
          </div>
        </div>

        {/* Feedback Message */}
        {feedback.message && (
          <div className={`mb-4 p-3 rounded-lg border flex items-center space-x-2 ${
            feedback.type === 'success' ?'bg-success/10 border-success/20 text-success' :'bg-destructive/10 border-destructive/20 text-destructive'
          }`}>
            <Icon 
              name={feedback.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
              size={16} 
            />
            <span className="text-sm font-medium">{feedback.message}</span>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex rounded-lg bg-muted p-1 mb-6">
          <button
            onClick={() => setActiveTab('add')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'add' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Add Minutes
          </button>
          <button
            onClick={() => setActiveTab('subtract')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'subtract' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Subtract Minutes
          </button>
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-foreground mb-3">Quick Actions</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {quickAmounts[activeTab].map((amount) => (
              <Button
                key={amount}
                variant="outline"
                onClick={() => handleQuickAction(activeTab, amount)}
                loading={isProcessing}
                disabled={activeTab === 'subtract' && amount > (client?.balance || 0)}
                className={`${
                  activeTab === 'add' ?'hover:bg-success hover:text-success-foreground hover:border-success' :'hover:bg-warning hover:text-warning-foreground hover:border-warning'
                } transition-all duration-150`}
              >
                {activeTab === 'add' ? '+' : '-'}{amount}min
              </Button>
            ))}
          </div>
        </div>

        {/* Custom Amount */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-foreground">Custom Amount</h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="customAmount" className="block text-sm font-medium text-muted-foreground mb-2">
                Minutes
              </label>
              <Input
                id="customAmount"
                type="number"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                min="1"
                max={activeTab === 'subtract' ? client?.balance || 0 : undefined}
              />
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-muted-foreground mb-2">
                Description (Optional)
              </label>
              <Input
                id="description"
                type="text"
                placeholder="Transaction description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={100}
              />
            </div>
          </div>

          <Button
            onClick={handleCustomTransaction}
            loading={isProcessing}
            disabled={!customAmount || (activeTab === 'subtract' && parseInt(customAmount) > (client?.balance || 0))}
            className={`w-full sm:w-auto ${
              activeTab === 'add' ?'bg-success hover:bg-success/90 text-success-foreground' :'bg-warning hover:bg-warning/90 text-warning-foreground'
            }`}
            iconName={activeTab === 'add' ? 'Plus' : 'Minus'}
            iconPosition="left"
          >
            {activeTab === 'add' ? 'Add' : 'Subtract'} {customAmount || '0'} Minutes
          </Button>
        </div>

        {/* Balance Warning */}
        {activeTab === 'subtract' && (client?.balance || 0) < 30 && (
          <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-center space-x-2 text-warning">
              <Icon name="AlertTriangle" size={16} />
              <span className="text-sm font-medium">
                Low balance warning: Client has only {client?.balance || 0} minutes remaining
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MinuteManagement;